import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import { clearPendingSsoLink, getPendingSsoLinkProvider } from '@/lib/auth/clearPendingSsoLink'
import type { SsoProviderKey } from '@/lib/auth/ssoProviders'

// User data types
export interface HomeAddress {
  line_1?: string
  line_2?: string
  city?: string
  state?: string
  zip?: string
  time_zone?: string
  latitude?: number
  longitude?: number
}

export interface User {
  id: string
  first_name: string
  last_name?: string
  email?: string
  phone_number?: string
  date_of_birth?: string
  sex_assigned_at_birth?: string
  id_verified?: boolean
  home_address?: HomeAddress | null
  home_address_time_zone?: string
  blocked: boolean
}

export interface Passkey {
  id: string
  name: string | null
  created_at: string
  authenticator_attachment: string | null
}

export interface PaymentMethod {
  id: string
  brand: string
  last_four: string
  exp_month: number
  exp_year: number
  is_default?: boolean
}

export interface Insurance {
  id: string
  payer_name: string
  member_id: string
  plan_name?: string | null
  external_state?: string
}

export interface UpdateProfileRequest {
  home_address?: {
    line_1: string
    line_2: string
    city: string
    state: string
    zip: string
  }
  sex_assigned_at_birth?: string
  email?: string
}

export interface MercyBillingResponse {
  guarantor_id: string
  billing_ids: string[]
  /** Single-use token bound to (visit_id, patient_id) for the Trust Commerce return POST. */
  billing_token: string
}

// User API slice
export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<User, void>({
      query: () => apiEndpoints.user.getProfile,
      providesTags: ['User'],
    }),
    updateProfile: builder.mutation<User, UpdateProfileRequest>({
      query: (body) => ({
        url: apiEndpoints.user.updateProfile,
        method: 'PATCH',
        body,
      }),
      // PatientRegistrationStatus: clear stale Mercy registration poll cache after profile updates.
      invalidatesTags: ['User', 'PatientRegistrationStatus'],
    }),
    getMercyBilling: builder.query<MercyBillingResponse, { visitId: string }>({
      query: ({ visitId }) => ({
        url: apiEndpoints.user.getMercyBilling,
        params: { visit_id: visitId },
      }),
      providesTags: ['MercyBilling'],
    }),
    getPaymentMethods: builder.query<PaymentMethod[], void>({
      query: () => apiEndpoints.user.getPaymentMethods,
      transformResponse: (response: PaymentMethod[] | { payment_methods: PaymentMethod[] }) => {
        // Handle both flat array and wrapped object responses
        if (Array.isArray(response)) return response
        if (response && 'payment_methods' in response) return response.payment_methods
        return []
      },
      providesTags: ['PaymentMethods'],
    }),
    getInsurances: builder.query<Insurance[], void>({
      query: () => apiEndpoints.insurances.getInsurances,
      transformResponse: (response: Insurance[] | { insurances: Insurance[] }) => {
        if (Array.isArray(response)) return response
        if (response && 'insurances' in response) return response.insurances
        return []
      },
      providesTags: ['Insurance'],
    }),
    deletePaymentMethod: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: apiEndpoints.user.deletePaymentMethod(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentMethods'],
    }),
    getSsoStatus: builder.query<{ linked: boolean; email?: string | null }, SsoProviderKey>({
      query: (provider) => apiEndpoints.user.getSsoStatus(provider),
      providesTags: (_result, _error, provider) => [{ type: 'SsoStatus', id: provider }],
      async onQueryStarted(provider, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // A pending "link this provider" prompt is moot once that provider is already linked.
          if (data.linked && getPendingSsoLinkProvider() === provider) clearPendingSsoLink()
        } catch {
          // Request failures are surfaced through the query state.
        }
      },
    }),
    startSsoOAuth: builder.mutation<{ authorize_url: string }, SsoProviderKey>({
      query: (provider) => ({
        url: apiEndpoints.user.startSsoOAuth(provider),
        method: 'POST',
      }),
    }),
    disconnectSso: builder.mutation<{ linked: boolean; email?: string | null }, SsoProviderKey>({
      query: (provider) => ({
        url: apiEndpoints.user.disconnectSso(provider),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, provider) => [
        { type: 'SsoStatus', id: provider },
        'SignInMethodEnrollment',
      ],
    }),
    getPasskeys: builder.query<{ passkeys: Passkey[] }, void>({
      query: () => apiEndpoints.user.getPasskeys,
      providesTags: ['Passkeys'],
    }),
    getSignInMethodEnrollment: builder.query<{ enrolled: boolean }, void>({
      query: () => apiEndpoints.user.getSignInMethodEnrollment,
      providesTags: ['SignInMethodEnrollment'],
    }),
    startPasskeyRegistration: builder.mutation<{ options: Record<string, unknown> }, void>({
      query: () => ({
        url: apiEndpoints.user.startPasskeyRegistration,
        method: 'POST',
      }),
    }),
    completePasskeyRegistration: builder.mutation<{ linked: boolean }, { credential: Record<string, unknown> }>({
      query: ({ credential }) => ({
        url: apiEndpoints.user.completePasskeyRegistration,
        method: 'POST',
        body: { credential },
      }),
      invalidatesTags: ['Passkeys', 'SignInMethodEnrollment'],
    }),
    deletePasskey: builder.mutation<void, { credentialId: string }>({
      query: ({ credentialId }) => ({
        url: apiEndpoints.user.deletePasskey(credentialId),
        method: 'DELETE',
      }),
      invalidatesTags: ['Passkeys', 'SignInMethodEnrollment'],
    }),
    revokeAllSessions: builder.mutation<{ revoked: boolean }, void>({
      query: () => ({
        url: apiEndpoints.user.revokeAllSessions,
        method: 'POST',
      }),
      // Nothing cached stays valid after a revocation, and the caller signs out immediately, so there is nothing to refetch.
      invalidatesTags: [],
    }),
  }),
})

// Export hooks for use in components
export const {
  useGetProfileQuery,
  useUpdateProfileMutation,
  useGetMercyBillingQuery,
  useGetPaymentMethodsQuery,
  useGetInsurancesQuery,
  useDeletePaymentMethodMutation,
  useGetSsoStatusQuery,
  useStartSsoOAuthMutation,
  useDisconnectSsoMutation,
  useGetPasskeysQuery,
  useGetSignInMethodEnrollmentQuery,
  useStartPasskeyRegistrationMutation,
  useCompletePasskeyRegistrationMutation,
  useDeletePasskeyMutation,
  useRevokeAllSessionsMutation,
} = userApi
